import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import {
    Card,
    CardHeader,
    CardContent,
    Avatar,
    Typography,
    Select,
    MenuItem,
    Switch,
    FormControlLabel,
    Checkbox,
    TextField,
    InputAdornment
} from '@material-ui/core';
import { WarningIconWithTooltip, AdditionalInfo } from '../../../../../../ZaddonsJSLibs/common-client2/src/components';
import { UPDATE_SETUP, RESET_INPUT_NOTIFICATIONS } from '../../../store/setup';
import {
    BidProcessType, ScheduleSource, AnchorDay, NotificationType
} from '../../../../Enums';
import { getAutoApprovalLabel, getBidProcessTypeLabel, getScheduleSourceLabel } from '../../../../Enums/Helpers/LabellingHelper';
import BreakCalculation from './BreakCalculation';
import './FunctionSection.scss';

export const SequentialOnlyNotificationIds = [
    NotificationType.sequentialTurnWillStartNotifIdToEmployee,
    NotificationType.sequentialTurnHasStartedNotifIdToEmployee,
    NotificationType.sequentialTurnHasEndedNotifIdToEmployee,
];

export const PreferentialOnlyNotificationIds = [
    NotificationType.preferentialDraftMadeNotifIdToEmployee,
    NotificationType.preferentialHasStartedNotifIdToEmployee,
    NotificationType.preferentialWillStartNotifIdToEmployee,
];

/** @param {string|boolean|number|null} value */
const isStringifiedBoolTruthy = (value) => {
    const stringified = String(value).toLocaleLowerCase();
    return !!value && (stringified === 'true' || value === '1');
};

const InputTitle = (props) => (
    <Typography
        component="p"
        className="input-label"
        {...props}
    >
        {props.children}
    </Typography>
);

const InputDisclaimer = (props) => (
    <p {...props}>
        <small className="input-disclaimer">
            {props.children}
        </small>
    </p>
);

const StyledSelect = (props) => (
    <Select
        MenuProps={{
            anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left',
            },
            transformOrigin: {
                vertical: 'top',
                horizontal: 'left',
            },
            getContentAnchorEl: null,
        }}
        variant="outlined"
        className="sue-select-full-width"
        defaultValue=""
        {...props}
    >
        {props.children}
    </Select>
);

/**
 * @param {{
 *  stepIndex: number
 *  validationErrors: {[Key: string] : boolean}
 *  disabledSections: {[Key: string] : boolean}
 *  errorRef: import('react').MutableRefObject<HTMLInputElement>
 * }} props
 */
function FunctionSection({
    stepIndex,
    validationErrors,
    disabledSections,
    errorRef,
}) {
    const dispatch = useDispatch();
    const { t } = useTranslation('shiftBidding', { keyPrefix: 'admin.setupEdit.functionsStep' });
    const { t: twarning } = useTranslation('shiftBidding', { keyPrefix: 'admin.warnings' });
    /** @type {ShiftBidding.AdminStore} */
    // @ts-ignore
    const { adminSetup, publicConfigs } = useSelector((state) => state);
    const {
        scheduleSource,
        bidProcessType,
        anchorDay,
        isOverrideSchedule,
        isRebidAllowed,
        isAllowEmployeesToLockForRebid,
        isAutoApproval,
        isAutoSignOff,
        weeklyHours,
        weeklyHoursByDefault,
        breakRules,
        isAllowBidOnActiveWorkplan,
        isEditEmployeesWhenBidActive,
        isGroupShiftlinesWithSameSchedules,
        isEmployeeCanBidAfterAllottedTime,
        sequentialBidClosingDelay
    } = adminSetup;
    const { isScheduleOverrideAvailable, isRebidAvailable, isUseUkgReadyTerminology } = publicConfigs;

    const schedulePatternLabel = t(isUseUkgReadyTerminology
        ? 'shiftBidding:common.terminology.schedulePattern_ukgReady'
        : 'shiftBidding:common.terminology.schedulePattern');

    /** @type {boolean} */
    const isWorkplanIntegrationEnabled = isStringifiedBoolTruthy(sessionStorage.getItem('isWorkplanIntegrationEnabled'));
    const isExcelScheduleSource = scheduleSource === ScheduleSource.ExcelFile;
    const isWorkplanScheduleSource = scheduleSource === ScheduleSource.WorkplanManager;

    /**
     * @typedef {{value: BidProcessType, label: string}} BidProcessTypeOption
     * @type {BidProcessTypeOption[]}
    */
    const bidProcessTypeOptions = [
        { value: BidProcessType.Sequential, label: getBidProcessTypeLabel(BidProcessType.Sequential) },
        { value: BidProcessType.Preferential, label: getBidProcessTypeLabel(BidProcessType.Preferential) }];
    const getBidProcessValue = () => (bidProcessType === -1 ? '' : bidProcessTypeOptions.find((x) => x.value === bidProcessType).value);
    /**
     * @typedef {{value: ScheduleSource, label: string, disabled: boolean}} ScheduleSourceOption
     * @type {ScheduleSourceOption[]}
     */
    const scheduleSourceOptions = [
        {
            value: ScheduleSource.ExcelFile,
            label: getScheduleSourceLabel(ScheduleSource.ExcelFile),
            disabled: false,
        },
        {
            value: ScheduleSource.WorkplanManager,
            label: getScheduleSourceLabel(ScheduleSource.WorkplanManager),
            disabled: !isWorkplanIntegrationEnabled,
        },
    ];
    const getScheduleSourceValue = () => (scheduleSource === -1 ? '' : scheduleSourceOptions.find((x) => x.value === scheduleSource).value);

    /**
     * @typedef {{value: number, label: string}} AnchorDayOptions
     * @type {AnchorDayOptions[]}
     */
    const anchorDayOptions = [
        { value: AnchorDay.Sunday, label: t('anchorDayInput.anchorDay.sunday') },
        { value: AnchorDay.Monday, label: t('anchorDayInput.anchorDay.monday') },
        { value: AnchorDay.Tuesday, label: t('anchorDayInput.anchorDay.tuesday') },
        { value: AnchorDay.Wednesday, label: t('anchorDayInput.anchorDay.wednesday') },
        { value: AnchorDay.Thursday, label: t('anchorDayInput.anchorDay.thursday') },
        { value: AnchorDay.Friday, label: t('anchorDayInput.anchorDay.friday') },
        { value: AnchorDay.Saturday, label: t('anchorDayInput.anchorDay.saturday') },
    ];

    const getAnchorDayValue = () => {
        const option = anchorDayOptions.find((x) => x.value === anchorDay);
        const value = option !== undefined ? option.value : '';
        return value;
    };

    /**
     * @typedef {{value: number, label: string, boolValue: boolean}} AutoApprovalOption
     * @type {AutoApprovalOption[]}
     */
    const autoApprovalOptions = [
        { value: 1, label: getAutoApprovalLabel(true), boolValue: true },
        { value: 0, label: getAutoApprovalLabel(false), boolValue: false }
    ];

    const getAutoApprovalValue = () => {
        const option = autoApprovalOptions.find((x) => x.boolValue === isAutoApproval);
        return option !== undefined ? option.value : '';
    };

    /**
     * @param {string} target
     * @param {BidProcessType | null | boolean} value
     */
    const updateSetupStore = (target, value) => {
        dispatch({
            type: UPDATE_SETUP,
            payload: {
                [target]: value,
            },
        });
    };

    /**
     * @param {ScheduleSource} value
     */
    const updateScheduleSource = (value) => {
        if (value === ScheduleSource.WorkplanManager) {
            dispatch({
                type: UPDATE_SETUP,
                payload: {
                    scheduleSource: value,
                    isOverrideSchedule: false,
                    anchorDay: AnchorDay.Sunday,
                    weeklyHours: false,
                    weeklyHoursByDefault: false,
                    breakRules: [],
                },
            });
            return;
        }
        if (value === ScheduleSource.ExcelFile && isRebidAllowed) {
            dispatch({
                type: UPDATE_SETUP,
                payload: {
                    scheduleSource: value,
                    isOverrideSchedule: true,
                    isAllowBidOnActiveWorkplan: false,
                },
            });
            return;
        }
        dispatch({
            type: UPDATE_SETUP,
            payload: {
                scheduleSource: value,
                isAllowBidOnActiveWorkplan: false,
            },
        });
    };

    /**
     * @param {BidProcessType} value
     */
    const updateProcessType = (value) => {
        if (value === BidProcessType.Preferential) {
            // Reset sequential-only values
            dispatch({
                type: UPDATE_SETUP,
                payload: {
                    bidProcessType: value,
                    isEmployeeCanBidAfterAllottedTime: true,
                    sequentialBidClosingDelay: 0,
                },
            });
            dispatch({ type: RESET_INPUT_NOTIFICATIONS, payload: SequentialOnlyNotificationIds });
            return;
        }
        // Reset preferential-only values
        dispatch({
            type: UPDATE_SETUP,
            payload: {
                bidProcessType: value,
                isRebidAllowed: false,
                isAllowEmployeesToLockForRebid: false,
                isEmployeeMustSubmitRequiredAmountOfBids: false,
                isRecalculateEmployeeRanksAllowedWhenActiveOrClosed: false,
            },
        });
        dispatch({ type: RESET_INPUT_NOTIFICATIONS, payload: PreferentialOnlyNotificationIds });
    };

    /**
     * @param {boolean} value
     */
    const updateIsRebidAllowed = (value) => {
        if (value && scheduleSource === ScheduleSource.ExcelFile) {
            dispatch({
                type: UPDATE_SETUP,
                payload: {
                    isRebidAllowed: value,
                    isOverrideSchedule: true,
                },
            });
            return;
        }
        dispatch({
            type: UPDATE_SETUP,
            payload: {
                isRebidAllowed: value,
                isAllowEmployeesToLockForRebid: false,
            },
        });
    };

    const updateIsAllowBidOnActiveWorkplan = (value) => {
        dispatch({
            type: UPDATE_SETUP,
            payload: {
                isAllowBidOnActiveWorkplan: (scheduleSource === ScheduleSource.WorkplanManager && value),
            },
        });
    };

    /**
     * @param {boolean} isAutomatic
     */
    const updateIsAutoApproval = (isAutomatic) => {
        if (isAutomatic) {
            dispatch({
                type: UPDATE_SETUP,
                payload: {
                    isAutoApproval: isAutomatic,
                },
            });
            return;
        }
        dispatch({
            type: UPDATE_SETUP,
            payload: {
                isAutoApproval: isAutomatic,
                isAutoSignOff: false,
            },
        });
    };

    // Set default schedule source to Excel File when Workplan is not enabled
    useEffect(() => {
        if (!isWorkplanIntegrationEnabled && scheduleSource === -1) {
            updateSetupStore('scheduleSource', ScheduleSource.ExcelFile);
        }
    }, [isWorkplanIntegrationEnabled, scheduleSource]);

    return (
        <Card className="card">
            <CardHeader
                ref={errorRef}
                className="card--header"
                color="textSecondary"
                gutterBottom
                avatar={<Avatar className="avatar">{stepIndex}</Avatar>}
                title={t('title')}
            />
            <CardContent>
                <div>
                    <InputTitle>{t('bidProcessTypeInput.label')}</InputTitle>
                    <StyledSelect
                        labelId="select-bid-process-type-label"
                        id="select-bid-process-type"
                        error={validationErrors.bidProcessType}
                        disabled={disabledSections.bidProcessType}
                        renderValue={(/** @type {BidProcessType} */
                            _value,
                        ) => bidProcessTypeOptions.find((x) => x.value === bidProcessType)?.label}
                        value={getBidProcessValue()}
                        onChange={(/** @type {React.ChangeEvent<{ name?: string; value: BidProcessType }>} */
                            event,
                        ) => updateProcessType(event.target.value)}
                    >
                        {bidProcessTypeOptions.map((x) => (
                            <MenuItem
                                value={x.value}
                                key={`SelectItem_${x.label}`}
                            >
                                {x.label}
                            </MenuItem>
                        ))}
                    </StyledSelect>
                    {
                        validationErrors.bidProcessType
                            ? <span style={{ color: 'red' }}>{t('bidProcessTypeInput.error')}</span> : null
                    }
                </div>
                <div>
                    <InputTitle>{t('scheduleSourceInput.label')}</InputTitle>
                    <StyledSelect
                        labelId="select-schedule-source-label"
                        id="select-schedule-source"
                        error={validationErrors.scheduleSource}
                        disabled={disabledSections.scheduleSource || !isWorkplanIntegrationEnabled}
                        renderValue={(/** @type {ScheduleSource} */
                            _value,
                        ) => scheduleSourceOptions.find((x) => x.value === scheduleSource)?.label}
                        value={getScheduleSourceValue()}
                        onChange={(/** @type {React.ChangeEvent<{ name?: string; value: ScheduleSource }>} */
                            event,
                        ) => updateScheduleSource(event.target.value)}
                    >
                        {scheduleSourceOptions.map((x) => (
                            <MenuItem
                                value={x.value}
                                disabled={x.disabled}
                                key={`SelectItem_${x.label}`}
                            >
                                {x.label}
                            </MenuItem>
                        ))}
                    </StyledSelect>
                    {
                        validationErrors.scheduleSource
                            ? <span style={{ color: 'red' }}>{t('scheduleSourceInput.error')}</span> : null
                    }
                </div>
                {isWorkplanScheduleSource && (
                    <div>
                        <FormControlLabel
                            control={(
                                <Switch
                                    checked={isAllowBidOnActiveWorkplan}
                                    disabled={disabledSections.isAllowBidOnActiveWorkplan}
                                    onChange={(event) => updateIsAllowBidOnActiveWorkplan(event.target.checked)}
                                    color="primary"
                                />
                            )}
                            label={t('isAllowBidOnActiveWorkplanInput.label')}
                            className="sue-form-control"
                        />
                        <InputDisclaimer>
                            {t('isAllowBidOnActiveWorkplanInput.disclaimer')}
                        </InputDisclaimer>
                    </div>
                )}

                {isExcelScheduleSource && (
                    <div>
                        <div>
                            <InputTitle>{t('anchorDayInput.label')}</InputTitle>
                            <StyledSelect
                                labelId="select-anchor-day-label"
                                id="select-anchor-day"
                                error={validationErrors.anchorDay}
                                disabled={disabledSections.anchorDay}
                                renderValue={(/** @type {number} */
                                    _value,
                                ) => anchorDayOptions.find((x) => x.value === anchorDay)?.label}
                                value={getAnchorDayValue()}
                                onChange={(/** @type {React.ChangeEvent<{ name?: string; value: number }>} */
                                    event,
                                ) => updateSetupStore('anchorDay', event.target.value)}
                            >
                                {anchorDayOptions.map((x) => (
                                    <MenuItem
                                        value={x.value}
                                        key={`SelectItem_${x.label}`}
                                    >
                                        {x.label}
                                    </MenuItem>
                                ))}
                            </StyledSelect>
                        </div>
                        {
                            validationErrors.anchorDay ? (
                                <span style={{ color: 'red' }}>{t('anchorDayInput.error')}</span>
                            ) : null
                        }
                    </div>
                )}
                {bidProcessType === BidProcessType.Preferential && isRebidAvailable ? (
                    <div>
                        <div className="function-section-rebids-allowed">
                            <FormControlLabel
                                control={(
                                    <Switch
                                        checked={isRebidAllowed}
                                        disabled={disabledSections.isRebidAllowed}
                                        onChange={(event) => updateIsRebidAllowed(event.target.checked)}
                                        color="primary"
                                    />
                                )}
                                label={t('isRebidAllowedInput.label')}
                                className="sue-form-control"
                            />
                            {
                                (isRebidAllowed) && (
                                    <WarningIconWithTooltip
                                        tooltipWarnings={[twarning('AutoArchiveDelayWithRebidAllowed')]}
                                        fontSize="medium"
                                    />
                                )
                            }
                        </div>
                        <InputDisclaimer>
                            {t('isRebidAllowedInput.disclaimer')}
                        </InputDisclaimer>
                    </div>
                ) : <></>}
                {isRebidAllowed ? (
                    <div>
                        <FormControlLabel
                            control={(
                                <Switch
                                    checked={isAllowEmployeesToLockForRebid}
                                    disabled={disabledSections.isAllowEmployeesToLockForRebid}
                                    onChange={(event) => updateSetupStore('isAllowEmployeesToLockForRebid', event.target.checked)}
                                    color="primary"
                                />
                            )}
                            label={t('isAllowEmployeesToLockForRebidInput.label')}
                            className="sue-form-control"
                        />
                        <InputDisclaimer>
                            {t('isAllowEmployeesToLockForRebidInput.disclaimer')}
                        </InputDisclaimer>
                    </div>
                ) : <></>}
                {
                    isExcelScheduleSource && isScheduleOverrideAvailable ? (
                        <div>
                            <FormControlLabel
                                control={(
                                    <Switch
                                        checked={isOverrideSchedule}
                                        disabled={disabledSections.isOverrideSchedule
                                            || (isRebidAllowed && scheduleSource === ScheduleSource.ExcelFile)}
                                        onChange={(event) => updateSetupStore('isOverrideSchedule', event.target.checked)}
                                        color="primary"
                                    />
                                )}
                                label={t('isOverrideScheduleInput.label')}
                                className="sue-form-control"
                            />
                            <InputDisclaimer>
                                {t('isOverrideScheduleInput.disclaimer', { schedulePattern: schedulePatternLabel.toLowerCase() })}
                            </InputDisclaimer>
                        </div>
                    ) : <></>
                }
                <div>
                    <InputTitle>{t('isAutoApprovalInput.label')}</InputTitle>
                    <StyledSelect
                        labelId="select-auto-approval-label"
                        id="select-auto-approval"
                        disabled={disabledSections.isAutoApproval}
                        renderValue={(/** @type {number} */
                            _value,
                        ) => autoApprovalOptions.find((x) => x.boolValue === isAutoApproval)?.label}
                        value={getAutoApprovalValue()}
                        onChange={(/** @type {React.ChangeEvent<{ name?: string; value: number }>} */
                            event,
                        ) => {
                            const selectedOption = autoApprovalOptions.find((x) => x.value === event.target.value);
                            if (selectedOption) {
                                updateIsAutoApproval(selectedOption.boolValue);
                            }
                        }}
                    >
                        {autoApprovalOptions.map((x) => (
                            <MenuItem
                                value={x.value}
                                key={`SelectItem_${x.label}`}
                            >
                                {x.label}
                            </MenuItem>
                        ))}
                    </StyledSelect>
                    <InputDisclaimer>
                        {t('isAutoApprovalInput.disclaimer')}
                    </InputDisclaimer>
                </div>
                <div>
                    <FormControlLabel
                        control={(
                            <Switch
                                checked={isAutoSignOff}
                                disabled={disabledSections.isAutoSignOff || !isAutoApproval}
                                onChange={(event) => updateSetupStore('isAutoSignOff', event.target.checked)}
                                color="primary"
                            />
                        )}
                        label={t('isAutoSignOffInput.label')}
                        className="sue-form-control"
                    />
                    <InputDisclaimer>
                        {t('isAutoSignOffInput.disclaimer')}
                    </InputDisclaimer>
                </div>
                <div>
                    <FormControlLabel
                        control={(
                            <Switch
                                checked={isEditEmployeesWhenBidActive}
                                disabled={disabledSections.isEditEmployeesWhenBidActive}
                                onChange={(event) => updateSetupStore('isEditEmployeesWhenBidActive', event.target.checked)}
                                color="primary"
                            />
                        )}
                        label={t('isEditEmployeesWhenBidActive.label')}
                        className="sue-form-control"
                    />
                </div>
                <div className="function-section-group-shiftlines">
                    <FormControlLabel
                        control={(
                            <Switch
                                checked={isGroupShiftlinesWithSameSchedules}
                                disabled={disabledSections.isGroupShiftlinesWithSameSchedules}
                                onChange={(event) => updateSetupStore('isGroupShiftlinesWithSameSchedules', event.target.checked)}
                                color="primary"
                            />
                        )}
                        label={t('isGroupShiftlinesWithSameSchedules.label')}
                        className="sue-form-control"
                    />
                    <AdditionalInfo className="additionalInfo">
                        {t('isGroupShiftlinesWithSameSchedules.tooltip')}
                    </AdditionalInfo>
                </div>
                {
                    isExcelScheduleSource ? (
                        <div>
                            <FormControlLabel
                                control={(
                                    <Switch
                                        checked={weeklyHours}
                                        onChange={(event) => updateSetupStore('weeklyHours', event.target.checked)}
                                        color="primary"
                                    />
                                )}
                                label={t('scheduledHoursInput.toggleLabel')}
                                className="sue-form-control"
                                disabled={disabledSections.weeklyHours}
                            />
                            {weeklyHours && (
                                <>
                                    <FormControlLabel
                                        key={`weeklyHoursByDefault_${true}`}
                                        control={(
                                            <Checkbox
                                                checked={weeklyHoursByDefault}
                                                onChange={(event) => updateSetupStore('weeklyHoursByDefault', event.target.checked)}
                                                color="primary"
                                            />
                                        )}
                                        label={(
                                            <Typography variant="body1">
                                                {t('scheduledHoursInput.checkboxLabel')}
                                            </Typography>
                                        )}
                                        className="weekly-hours-default"
                                        disabled={disabledSections.weeklyHours}
                                    />
                                    <BreakCalculation
                                        breakRules={breakRules}
                                        handleChangeSetting={updateSetupStore}
                                        disabled={disabledSections.weeklyHours}
                                    />
                                </>
                            )}
                        </div>
                    ) : <></>
                }
                {
                    bidProcessType === BidProcessType.Sequential ? (
                        <div>
                            <div>
                                <FormControlLabel
                                    control={(
                                        // Logic is reversed here because the label changed. TODO rename change when we have some time
                                        <Switch
                                            checked={!isEmployeeCanBidAfterAllottedTime}
                                            disabled={disabledSections.isEmployeeCanBidAfterAllottedTime}
                                            onChange={
                                                (event) => updateSetupStore('isEmployeeCanBidAfterAllottedTime', !event.target.checked)
                                            }
                                            color="primary"
                                        />
                                    )}
                                    label={t('isEmployeeCanBidAfterAllottedTime.label')}
                                    className="sue-form-control"
                                />
                                <InputDisclaimer>
                                    {t('isEmployeeCanBidAfterAllottedTime.disclaimer')}
                                </InputDisclaimer>
                            </div>
                            <div style={{ marginTop: '15px' }}>
                                <InputTitle className="function-input-title-sequentialBidClosingDelay">
                                    {t('sequentialBidClosingDelay.label')}
                                </InputTitle>
                                <div>
                                    <div className="function-textfield-number">
                                        <TextField // Hours
                                            value={Math.floor(sequentialBidClosingDelay / 60)}
                                            type="number"
                                            variant="outlined"
                                            size="small"
                                            InputProps={{
                                                endAdornment: <InputAdornment position="end">H</InputAdornment>,
                                            }}
                                            onChange={(event) => {
                                                const minutes = sequentialBidClosingDelay % 60 || 0;
                                                let totalHourInMinutes = parseInt(event.target.value, 10) * 60 + minutes;
                                                if (totalHourInMinutes < 0) {
                                                    totalHourInMinutes = 0;
                                                }
                                                updateSetupStore('sequentialBidClosingDelay', totalHourInMinutes);
                                            }}
                                        />
                                    </div>
                                    <div className="function-textfield-number">
                                        <TextField // Minutes
                                            value={(sequentialBidClosingDelay % 60)}
                                            type="number"
                                            variant="outlined"
                                            size="small"
                                            InputProps={{
                                                endAdornment: <InputAdornment position="end">M</InputAdornment>,
                                            }}
                                            onChange={(event) => {
                                                const hours = Math.floor(sequentialBidClosingDelay / 60) || 0;
                                                let totalHourInMinutes = parseInt(event.target.value, 10) + hours * 60;
                                                if (totalHourInMinutes < 0) {
                                                    totalHourInMinutes = 0;
                                                }
                                                updateSetupStore('sequentialBidClosingDelay', totalHourInMinutes);
                                            }}
                                        />
                                    </div>
                                </div>
                                <InputDisclaimer>
                                    {t('sequentialBidClosingDelay.disclaimer')}
                                </InputDisclaimer>
                            </div>
                        </div>
                    ) : <></>
                }
            </CardContent>
        </Card>
    );
}

export default FunctionSection;
